<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, inject, proxyRefs, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'commissions'),
    screen: computed(() => 'create'),
  })
);

const searchQuery = ref('');
const createdAgreement = ref(null);

const asArray = value => (Array.isArray(value) ? value : []);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const optionLabel = value => {
  const text = String(value || '').replaceAll('_', ' ');
  return text ? `${text[0].toLocaleUpperCase('pt-BR')}${text.slice(1)}` : '—';
};
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(item => item[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const imageUrlFrom = value => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return imageUrlFrom(value[0]);
  if (!value || typeof value !== 'object') return '';

  return imageUrlFrom(
    firstValue(value, [
      'url',
      'src',
      'image_url',
      'imageUrl',
      'preview_url',
      'file_url',
    ])
  );
};
const money = value => {
  if (value === '' || value === null || value === undefined) return '—';
  if (typeof value === 'object') {
    if (value.formatted || value.display || value.label) {
      return value.formatted || value.display || value.label;
    }
    return money(value.amount ?? value.value ?? value.price_amount);
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(numeric);
};
const numberValue = value => {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'object') {
    return numberValue(value.amount ?? value.value ?? value.price_amount);
  }

  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};
const formatDateTime = value => {
  if (!value) return 'Data não informada';

  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const createOptions = computed(() => realData.data.value?.createOptions || {});
const listings = computed(() => asArray(createOptions.value.listings));
const titleFor = listing =>
  firstValue(listing, ['titulo', 'title', 'name', 'reference', 'code']);
const locationFor = listing =>
  firstValue(listing, [
    'address.full_address',
    'endereco_completo',
    'endereco',
    'address.street',
    'address',
    'location',
  ]);
const amountFor = listing =>
  firstValue(listing, [
    'listing_price',
    'price_amount',
    'valor_venda',
    'sale_price',
    'value',
    'amount',
  ]);
const imageFor = listing =>
  imageUrlFrom(
    firstValue(listing, [
      'cover_url',
      'image_url',
      'thumbnail',
      'midias',
      'media',
      'images',
      'imovel.cover_url',
      'imovel.image_url',
      'property.cover_url',
      'property.image_url',
    ])
  );
const catalog = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');
  if (!query) return listings.value;

  return listings.value.filter(listing =>
    `${titleFor(listing)} ${locationFor(listing)}`
      .toLocaleLowerCase('pt-BR')
      .includes(query)
  );
});
const selectedListing = computed(() =>
  listings.value.find(
    listing => String(listing.id) === String(model.form.imovel_listing_id)
  )
);
const selectedListingImage = computed(() =>
  selectedListing.value ? imageFor(selectedListing.value) : ''
);
const selectedListingAmount = computed(() =>
  selectedListing.value ? amountFor(selectedListing.value) : null
);

const beneficiaryIdKey = computed(() =>
  model.form.beneficiary_kind === 'equipe'
    ? 'beneficiary_team_id'
    : 'beneficiary_user_id'
);
const beneficiaryId = computed(() => model.form[beneficiaryIdKey.value]);
const beneficiaryLabel = computed(() => {
  if (model.form.beneficiary_kind === 'imobiliaria') return 'Imobiliária';
  if (!beneficiaryId.value) return 'Informe o ID do beneficiário';

  return model.form.beneficiary_kind === 'equipe'
    ? `Equipe #${beneficiaryId.value}`
    : `Usuário #${beneficiaryId.value}`;
});
const beneficiaryComplete = computed(() =>
  model.form.beneficiary_kind === 'imobiliaria'
    ? true
    : Boolean(beneficiaryId.value)
);
const commissionValueComplete = computed(() => {
  const key =
    model.form.commission_kind === 'fixed'
      ? 'commission_amount'
      : 'commission_percentage';

  return numberValue(model.form[key]) !== null;
});
const requiredFieldsComplete = computed(
  () =>
    Boolean(model.form.imovel_listing_id) &&
    Boolean(model.form.side) &&
    Boolean(model.form.payment_event) &&
    beneficiaryComplete.value &&
    commissionValueComplete.value
);
const canCreateAgreement = computed(
  () =>
    model.canCreate && requiredFieldsComplete.value && !createdAgreement.value
);
const estimatedCommission = computed(() => {
  if (model.form.commission_kind === 'fixed') {
    return numberValue(model.form.commission_amount);
  }

  const baseAmount = numberValue(selectedListingAmount.value);
  const percentage = numberValue(model.form.commission_percentage);
  if (baseAmount === null || percentage === null) return null;

  return (baseAmount * percentage) / 100;
});
const commissionRule = computed(() => {
  if (model.form.commission_kind === 'fixed') {
    return money(model.form.commission_amount);
  }
  if (model.form.commission_percentage === '') return '—';

  return `${model.form.commission_percentage}%`;
});
const summaryRows = computed(() => [
  {
    label: 'Valor do anúncio',
    value: money(selectedListingAmount.value),
  },
  {
    label: 'Tipo de comissão',
    value: optionLabel(model.form.commission_kind),
  },
  { label: 'Regra cadastrada', value: commissionRule.value },
  {
    label: 'Beneficiário',
    value: beneficiaryComplete.value ? '1 acordo' : 'Aguardando',
  },
  { label: 'Rateio', value: 'Não suportado' },
]);
const auditEntries = computed(() =>
  asArray(createdAgreement.value?.versions).map((event, index) => {
    const actorId = firstValue(event, ['actor_id']);
    const changeKind = String(firstValue(event, ['change_kind'], 'registered'));
    const labels = {
      activated: 'Acordo publicado',
      created: 'Rascunho criado',
      settled: 'Comissão liquidada',
      updated: 'Alterações registradas',
    };

    return {
      actor: actorId ? `Usuário #${actorId}` : optionLabel(event.actor_type),
      date: formatDateTime(firstValue(event, ['recorded_at', 'created_at'])),
      id: event.version_number || event.id || `${changeKind}-${index}`,
      label: labels[changeKind] || optionLabel(changeKind),
    };
  })
);
const unavailableDocuments = [
  'Contrato de venda',
  'Termo de rateio',
  'Comprovante',
];
const stages = [
  ['Venda', 'Selecione o anúncio'],
  ['Beneficiário', 'Defina o recebedor'],
  ['Comissão', 'Informe a regra'],
  ['Documentos', 'Indisponível'],
  ['Revisão', 'Crie o rascunho'],
];

const selectListing = listing => {
  model.form.imovel_listing_id = listing.id;
};
const createAgreement = async () => {
  if (!canCreateAgreement.value) return;

  const response = await model.create();
  if (response) createdAgreement.value = response;
};

watch(
  () => model.form.beneficiary_kind,
  kind => {
    if (kind === 'corretor') model.form.beneficiary_team_id = '';
    if (kind === 'equipe') model.form.beneficiary_user_id = '';
    if (kind === 'imobiliaria') {
      model.form.beneficiary_team_id = '';
      model.form.beneficiary_user_id = '';
    }
  }
);
watch(
  () => model.form.commission_kind,
  kind => {
    if (kind === 'percentage') model.form.commission_amount = '';
    if (kind === 'fixed') model.form.commission_percentage = '';
  }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    data-imudei-visual-id="commission-create-root"
    class="relative mx-auto w-full max-w-[1284px] translate-x-[11px] pb-[148px] pt-[95px] text-[#101958]"
  >
    <header class="flex items-center gap-4 px-5">
      <div>
        <p class="text-[9px] font-semibold text-[#5a24c7]">
          Comissões / Nova comissão
        </p>
        <div class="mt-1 flex items-center gap-4">
          <h1 class="text-[22px] font-bold tracking-[-0.035em]">
            Nova comissão
          </h1>
          <span
            class="rounded-full bg-violet-50 px-3 py-1.5 text-[8px] font-semibold text-[#5a24c7]"
          >
            {{ createdAgreement ? 'Rascunho criado' : 'Etapa 1 de 5' }}
          </span>
        </div>
        <p class="mt-1 text-[9px] text-[#69759c]">
          Crie um acordo de comissão versionado para um anúncio da conta.
        </p>
      </div>
    </header>

    <section
      data-imudei-visual-id="commission-create-progress"
      class="mt-1 rounded-2xl border border-[#e8e4ef] bg-white p-4"
    >
      <div class="grid grid-cols-5 gap-2">
        <span
          v-for="(stage, index) in stages"
          :key="stage[0]"
          class="flex items-center gap-2"
        >
          <i
            class="grid size-7 place-items-center rounded-full text-[8px] font-bold"
            :class="
              index === 0
                ? 'bg-[#5a24c7] text-white'
                : 'bg-[#f0edf6] text-[#8c96b2]'
            "
            >{{ index + 1 }}</i
          >
          <span class="min-w-0">
            <strong
              class="block truncate text-[8px]"
              :class="index === 0 ? 'text-[#5a24c7]' : ''"
              >{{ stage[0] }}</strong
            >
            <small class="block truncate text-[7px] text-[#8490ad]">
              {{ stage[1] }}
            </small>
          </span>
          <i
            v-if="index < stages.length - 1"
            class="i-lucide-chevron-right ml-auto size-3 text-[#c3c9d8]"
          />
        </span>
      </div>
    </section>

    <section class="mt-4 grid grid-cols-[minmax(0,1fr)_308px] gap-4">
      <div class="grid grid-cols-[291px_minmax(0,1fr)] items-start gap-4 pl-4">
        <article
          class="min-h-[438px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <header class="flex items-center justify-between gap-3">
            <div>
              <h2 class="text-[14px] font-bold">Venda selecionada</h2>
              <p class="mt-1 text-[9px] text-[#7480a0]">
                Vincule o acordo ao anúncio que receberá a comissão.
              </p>
            </div>
            <span class="text-[8px] font-semibold text-[#68749b]">
              {{
                listings.length ? `${listings.length} anúncios` : 'Sem dados'
              }}
            </span>
          </header>

          <div
            v-if="selectedListing"
            class="mt-4 flex items-center gap-3 rounded-xl border border-[#cdbdff] bg-violet-50/50 p-3"
          >
            <img
              v-if="selectedListingImage"
              :src="selectedListingImage"
              :alt="titleFor(selectedListing)"
              class="size-12 rounded-xl object-cover"
            />
            <span
              v-else
              class="grid size-12 place-items-center rounded-xl bg-violet-100 text-[#5a24c7]"
            >
              <i class="i-lucide-building-2 size-5" />
            </span>
            <span class="min-w-0 flex-1">
              <strong class="block truncate text-[10px]">
                {{
                  titleFor(selectedListing) || `Anúncio #${selectedListing.id}`
                }}
              </strong>
              <small class="mt-1 block truncate text-[8px] text-[#7480a0]">
                {{ locationFor(selectedListing) || 'Endereço não informado' }}
              </small>
            </span>
            <span class="text-right">
              <small class="block text-[7px] text-[#7480a0]">
                Valor do anúncio
              </small>
              <b class="block text-[11px] text-[#5a24c7]">
                {{ money(selectedListingAmount) }}
              </b>
            </span>
          </div>
          <div
            v-else
            class="mt-4 flex min-h-20 items-center gap-3 rounded-xl border border-dashed border-[#d8d1e7] bg-[#faf9fd] p-3 text-[#7d87a4]"
          >
            <i
              class="i-lucide-building-2 grid size-12 place-items-center rounded-xl bg-[#f1eff7] text-[#9099b3]"
            />
            <span>
              <strong class="block text-[9px]">Anúncio não selecionado</strong>
              <small class="mt-1 block text-[8px]"
                >Escolha uma opção retornada pela API.</small
              >
            </span>
          </div>

          <label
            class="mt-4 flex h-9 items-center gap-2 rounded-lg border border-[#e3deeb] px-3 text-[8px] text-[#7480a0]"
          >
            <i class="i-lucide-search size-3.5" />
            <input
              v-model="searchQuery"
              class="min-w-0 flex-1 bg-transparent outline-none placeholder:text-[#9aa2b8]"
              placeholder="Buscar anúncio por nome ou endereço"
              type="search"
            />
          </label>
          <div class="mt-3 grid grid-cols-2 gap-2">
            <button
              v-for="listing in catalog.slice(0, 6)"
              :key="listing.id"
              class="flex min-w-0 items-center gap-2 rounded-xl border px-2 py-2 text-left transition"
              :class="
                String(listing.id) === String(model.form.imovel_listing_id)
                  ? 'border-[#5a24c7] bg-violet-50 ring-1 ring-violet-100'
                  : 'border-[#eeeaf4] hover:border-violet-300'
              "
              type="button"
              @click="selectListing(listing)"
            >
              <img
                v-if="imageFor(listing)"
                :src="imageFor(listing)"
                :alt="titleFor(listing)"
                class="size-8 rounded-lg object-cover"
              />
              <span
                v-else
                class="grid size-8 shrink-0 place-items-center rounded-lg bg-[#f1eff7] text-[#7d87a4]"
              >
                <i class="i-lucide-house size-3.5" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">
                  {{ titleFor(listing) || `Anúncio #${listing.id}` }}
                </strong>
                <small class="mt-0.5 block truncate text-[7px] text-[#7480a0]">
                  {{ locationFor(listing) || 'Localização não informada' }}
                </small>
              </span>
            </button>
            <span
              v-if="!catalog.length"
              class="col-span-2 flex min-h-14 items-center gap-2 rounded-xl border border-dashed border-[#d8d1e7] bg-[#faf9fd] px-3 text-[8px] text-[#7d87a4]"
            >
              <i class="i-lucide-cloud-off size-4" />
              Nenhum anúncio disponível para esta busca.
            </span>
          </div>
        </article>

        <article
          class="min-h-[518px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <header class="flex items-center justify-between gap-3">
            <div>
              <h2 class="text-[14px] font-bold">Participante da comissão</h2>
              <p class="mt-1 text-[9px] text-[#7480a0]">
                A API registra um beneficiário por acordo de comissão.
              </p>
            </div>
            <span
              class="rounded-lg border border-dashed border-[#d8d1e7] px-3 py-1.5 text-[8px] font-semibold text-[#7d87a4]"
            >
              <i class="i-lucide-users-round mr-1 inline size-3" />Rateio
              indisponível
            </span>
          </header>

          <div
            class="mt-4 grid grid-cols-[1.35fr_0.8fr_0.7fr_0.85fr] gap-3 px-2 text-[7px] text-[#7480a0]"
          >
            <span>Beneficiário</span><span>Tipo</span><span>Lado</span
            ><span>Evento</span>
          </div>
          <div
            class="mt-2 grid grid-cols-[1.35fr_0.8fr_0.7fr_0.85fr] items-center gap-3 rounded-xl border border-[#eeeaf4] px-2 py-3"
          >
            <span class="flex min-w-0 items-center gap-2">
              <i
                class="grid size-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-pink-400 to-violet-600 text-[7px] font-bold text-white"
                >{{ initials(beneficiaryLabel) }}</i
              >
              <label class="min-w-0 flex-1">
                <span class="sr-only">ID do beneficiário</span>
                <input
                  v-if="model.form.beneficiary_kind !== 'imobiliaria'"
                  v-model="model.form[beneficiaryIdKey]"
                  class="w-full rounded-lg border border-[#e3deeb] px-2 py-2 text-[8px] outline-none focus:border-[#5a24c7]"
                  min="1"
                  placeholder="ID do beneficiário"
                  type="number"
                />
                <span
                  v-else
                  class="block truncate rounded-lg border border-[#e3deeb] bg-[#faf9fd] px-2 py-2 text-[8px]"
                >
                  Imobiliária da conta
                </span>
              </label>
            </span>
            <label>
              <span class="sr-only">Tipo do beneficiário</span>
              <select
                v-model="model.form.beneficiary_kind"
                class="w-full rounded-lg border border-[#e3deeb] bg-white px-2 py-2 text-[8px] outline-none focus:border-[#5a24c7]"
              >
                <option value="corretor">Corretor</option>
                <option value="equipe">Equipe</option>
                <option value="imobiliaria">Imobiliária</option>
              </select>
            </label>
            <label>
              <span class="sr-only">Lado da negociação</span>
              <select
                v-model="model.form.side"
                class="w-full rounded-lg border border-[#e3deeb] bg-white px-2 py-2 text-[8px] outline-none focus:border-[#5a24c7]"
              >
                <option value="seller">Vendedor</option>
                <option value="buyer">Comprador</option>
              </select>
            </label>
            <label>
              <span class="sr-only">Evento de pagamento</span>
              <select
                v-model="model.form.payment_event"
                class="w-full rounded-lg border border-[#e3deeb] bg-white px-2 py-2 text-[8px] outline-none focus:border-[#5a24c7]"
              >
                <option value="on_closing">Fechamento</option>
                <option value="on_signing">Assinatura</option>
                <option value="on_deed">Escritura</option>
                <option value="on_first_rent">Primeiro aluguel</option>
                <option value="on_installments">Parcelas</option>
                <option value="custom">Personalizado</option>
              </select>
            </label>
          </div>

          <div class="mt-3 grid grid-cols-[1fr_1fr] gap-3">
            <label class="rounded-xl border border-[#eeeaf4] p-3">
              <span class="block text-[8px] font-semibold"
                >Cálculo da comissão</span
              >
              <select
                v-model="model.form.commission_kind"
                class="mt-2 h-9 w-full rounded-lg border border-[#e3deeb] bg-white px-2 text-[8px] outline-none focus:border-[#5a24c7]"
              >
                <option value="percentage">Percentual</option>
                <option value="fixed">Valor fixo</option>
              </select>
            </label>
            <label class="rounded-xl border border-[#eeeaf4] p-3">
              <span class="block text-[8px] font-semibold">
                {{
                  model.form.commission_kind === 'fixed'
                    ? 'Valor da comissão'
                    : 'Percentual da comissão'
                }}
              </span>
              <span
                class="mt-2 flex h-9 items-center rounded-lg border border-[#e3deeb] px-2"
              >
                <input
                  v-if="model.form.commission_kind === 'fixed'"
                  v-model="model.form.commission_amount"
                  class="min-w-0 flex-1 bg-transparent text-[8px] outline-none"
                  min="0"
                  placeholder="0,00"
                  step="0.01"
                  type="number"
                />
                <input
                  v-else
                  v-model="model.form.commission_percentage"
                  class="min-w-0 flex-1 bg-transparent text-[8px] outline-none"
                  max="100"
                  min="0"
                  placeholder="0"
                  step="0.01"
                  type="number"
                />
                <small class="text-[8px] text-[#7480a0]">
                  {{ model.form.commission_kind === 'fixed' ? 'BRL' : '%' }}
                </small>
              </span>
            </label>
          </div>

          <div
            class="mt-4 flex items-center justify-between rounded-xl border border-[#e3deeb] bg-[#faf9fd] px-4 py-3"
          >
            <span>
              <strong class="block text-[9px]"
                >Distribuição entre participantes</strong
              >
              <small class="mt-1 block text-[8px] text-[#7480a0]">
                A rota atual não recebe percentuais de rateio.
              </small>
            </span>
            <b class="text-[10px] text-[#7d87a4]">Indisponível</b>
          </div>
        </article>

        <article
          class="col-span-2 hidden rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <header class="flex items-center justify-between">
            <div>
              <h2 class="text-[11px] font-bold">Documentos de apoio</h2>
              <p class="mt-1 text-[8px] text-[#7480a0]">
                A criação de documentos não é oferecida por esta rota.
              </p>
            </div>
            <span class="text-[8px] font-semibold text-[#7d87a4]"
              >Sem endpoint</span
            >
          </header>
          <div class="mt-4 grid grid-cols-3 gap-3">
            <span
              v-for="document in unavailableDocuments"
              :key="document"
              class="rounded-xl border border-dashed border-[#d7ccef] bg-[#faf9fd] p-4 text-center text-[#7d87a4]"
            >
              <i class="i-lucide-file-x2 mx-auto block size-5" />
              <strong class="mt-2 block text-[8px]">{{ document }}</strong>
              <small class="mt-1 block text-[7px]"
                >Indisponível nesta API</small
              >
            </span>
          </div>
        </article>

        <article
          class="col-span-2 min-h-[106px] rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <header class="flex items-center justify-between">
            <div>
              <h2 class="text-[11px] font-bold">Feed auditável</h2>
              <p class="mt-1 text-[8px] text-[#7480a0]">
                Eventos retornados pelo ledger versionado do acordo.
              </p>
            </div>
            <span class="text-[8px] font-semibold text-[#7d87a4]">
              {{
                auditEntries.length
                  ? `${auditEntries.length} registro(s)`
                  : 'Aguardando criação'
              }}
            </span>
          </header>
          <div v-if="auditEntries.length" class="mt-4 grid grid-cols-3 gap-3">
            <span
              v-for="entry in auditEntries"
              :key="entry.id"
              class="flex min-w-0 gap-2 border-r border-[#eeeaf4] pr-3 last:border-0"
            >
              <i
                class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]"
                >{{ initials(entry.actor) }}</i
              >
              <span class="min-w-0">
                <strong class="block truncate text-[8px]">{{
                  entry.actor
                }}</strong>
                <small class="mt-1 block truncate text-[7px] text-[#7480a0]">{{
                  entry.label
                }}</small>
                <small class="mt-1 block text-[7px] text-[#8b94ad]">{{
                  entry.date
                }}</small>
              </span>
            </span>
          </div>
          <div
            v-else
            class="mt-4 flex min-h-16 items-center gap-3 rounded-xl border border-dashed border-[#d8d1e7] bg-[#faf9fd] px-3 text-[8px] text-[#7d87a4]"
          >
            <i class="i-lucide-shield-check size-5" />
            A primeira versão será exibida quando o rascunho for criado pela
            API.
          </div>
        </article>
      </div>

      <aside class="-mt-[152px] space-y-3">
        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Resumo da comissão</h2>
          <p
            v-for="item in summaryRows"
            :key="item.label"
            class="mt-4 flex items-center justify-between gap-3 text-[8px]"
          >
            <span class="text-[#7480a0]">{{ item.label }}</span>
            <b class="text-right">{{ item.value }}</b>
          </p>
          <div class="mt-4 rounded-xl bg-violet-50 p-3">
            <small class="block text-[7px] text-[#7480a0]">
              Valor estimado da comissão
            </small>
            <strong class="mt-1 block text-[14px] text-[#5a24c7]">
              {{
                estimatedCommission === null ? '—' : money(estimatedCommission)
              }}
            </strong>
            <small class="mt-1 block text-[7px] text-[#7480a0]">
              Baseado no valor retornado para o anúncio.
            </small>
          </div>
        </article>

        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Validação do acordo</h2>
          <div class="mt-4 flex gap-2">
            <i
              class="grid size-7 place-items-center rounded-full"
              :class="
                requiredFieldsComplete
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-[#f0edf6] text-[#7d87a4]'
              "
            >
              <i
                :class="
                  requiredFieldsComplete
                    ? 'i-lucide-check'
                    : 'i-lucide-circle-dot-dashed'
                "
                class="size-3.5"
              />
            </i>
            <span>
              <strong class="block text-[8px]">
                {{
                  requiredFieldsComplete
                    ? 'Campos suportados preenchidos'
                    : 'Aguardando dados do acordo'
                }}
              </strong>
              <small class="mt-1 block text-[7px] text-[#7480a0]">
                {{
                  requiredFieldsComplete
                    ? 'Pronto para criar um rascunho versionado.'
                    : 'Selecione o anúncio e complete as regras disponíveis.'
                }}
              </small>
            </span>
          </div>
          <div
            class="mt-4 rounded-xl border border-dashed border-[#d8d1e7] bg-[#faf9fd] p-3"
          >
            <strong class="block text-[8px] text-[#7d87a4]"
              >Rateio entre participantes</strong
            >
            <small class="mt-1 block text-[7px] leading-4 text-[#8b94ad]">
              Sem endpoint para cadastrar ou validar participações nesta
              criação.
            </small>
          </div>
        </article>

        <article
          class="rounded-2xl border border-[#dfd4f7] bg-gradient-to-br from-white to-[#fff0fb] p-4"
        >
          <h2 class="text-[10px] font-bold">Missão diária</h2>
          <p class="mt-3 text-[8px] text-[#69759c]">
            A pontuação de gamificação não é retornada por esta rota.
          </p>
          <div class="mt-3 h-1.5 rounded-full bg-violet-100">
            <div class="h-full w-0 rounded-full bg-[#5a24c7]" />
          </div>
          <small class="mt-2 block text-[7px] text-[#7480a0]">
            Pontos e progresso indisponíveis
          </small>
        </article>
      </aside>
    </section>
  </main>

  <p
    v-if="model.unavailableMessage"
    class="fixed bottom-[176px] right-7 z-50 max-w-sm rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-[9px] leading-4 text-[#6540a8]"
  >
    {{ model.unavailableMessage }}
  </p>
  <p
    v-if="model.localError || error"
    class="fixed bottom-[176px] right-7 z-50 max-w-sm rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-[9px] leading-4 text-rose-600"
  >
    {{ model.localError || error }}
  </p>

  <footer
    data-imudei-visual-id="commission-create-footer"
    class="fixed bottom-[114px] right-[38px] z-40 flex h-[46px] items-center justify-end gap-3"
  >
    <button
      class="rounded-lg border border-[#e5e0ed] px-6 py-2.5 text-[9px]"
      type="button"
      @click="emit('navigate', 'dashboard')"
    >
      Cancelar
    </button>
    <span v-if="model.notice" class="text-[8px] text-[#69759c]">
      {{ model.notice }}
    </span>
    <button
      class="rounded-lg bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-7 py-2.5 text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="saving || !canCreateAgreement"
      type="button"
      @click="createAgreement"
    >
      {{
        saving
          ? 'Criando rascunho…'
          : createdAgreement
            ? 'Rascunho criado'
            : 'Criar rascunho'
      }}
    </button>
  </footer>
</template>
